import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Pagination } from './ui/pagination';
import { Plus, Search, Eye, Edit, Trash2, Workflow } from 'lucide-react';
import { processesApiService, Process } from '../services/processesApi';
import { PermissionGate } from './ui/PermissionGate';

function getProcessWorkAreaLabel(process: Process): string {
  return (
    process.workAreaName ||
    process.workArea?.workAreaName ||
    process.workArea?.name ||
    (process.workAreaId ? `Work area #${process.workAreaId}` : '-')
  );
}

export function ProcessListingPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [processes, setProcesses] = useState<Process[]>([]);
  const [loading, setLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const ROUTE = '/processes';

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await processesApiService.getProcesses();
        setProcesses(Array.isArray(data) ? data : []);
      } catch {
        // toasted
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return processes.filter(
      (p) =>
        p.processName?.toLowerCase().includes(term) ||
        (p.description || '').toLowerCase().includes(term) ||
        getProcessWorkAreaLabel(p).toLowerCase().includes(term)
    );
  }, [processes, searchTerm]);

  const totalPages = Math.ceil(filtered.length / pageSize) || 1;
  const startIndex = (currentPage - 1) * pageSize;
  const paginated = filtered.slice(startIndex, startIndex + pageSize);

  const handleNew = () => navigate('/processes/new');
  const handleEdit = (p: Process) => navigate(`/processes/${p.id}/edit`);
  const handleView = (p: Process) => navigate(`/processes/${p.id}/view`);
  const handleDelete = async (p: Process) => {
    if (!p.id) return;
    if (window.confirm(`Delete process ${p.processName}?`)) {
      try {
        await processesApiService.deleteProcess(String(p.id));
        setProcesses((prev) => prev.filter((x) => x.id !== p.id));
      } catch {
        // toasted
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
          <span>Loading processes...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
        <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-primary-200 flex items-center justify-center">
                <Workflow className="h-5 w-5 text-primary-700" />
              </div>
              <div>
                <CardTitle className="text-xl text-gray-900">Process Master</CardTitle>
                <p className="text-gray-600 text-sm">Manage production processes</p>
              </div>
            </div>
            <PermissionGate route={ROUTE} action="create">
              <Button onClick={handleNew} className="bg-primary-500 text-gray-700 hover:bg-gray-50 shadow-lg">
                <Plus className="h-4 w-4 mr-2" />
                New Process
              </Button>
            </PermissionGate>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search processes..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="pl-10 border-primary-200 focus:border-primary-400 focus:ring-primary-400"
              />
            </div>
            <div className="text-sm text-gray-600">
              {filtered.length} of {processes.length} processes
            </div>
          </div>

          <div className="border border-primary-200 rounded-lg overflow-hidden bg-white">
            <Table>
              <TableHeader>
                <TableRow className="bg-primary-50">
                  <TableHead className="font-semibold text-primary-800">Process Name</TableHead>
                  <TableHead className="font-semibold text-primary-800">Description</TableHead>
                  <TableHead className="font-semibold text-primary-800">Work Area</TableHead>
                  <TableHead className="font-semibold text-primary-800">Status</TableHead>
                  <TableHead className="font-semibold text-primary-800 text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginated.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                      No processes found
                    </TableCell>
                  </TableRow>
                ) : (
                  paginated.map((p) => (
                    <TableRow key={p.id} className="hover:bg-primary-50/50">
                      <TableCell className="font-medium">{p.processName}</TableCell>
                      <TableCell className="max-w-[280px] truncate" title={p.description}>
                        {p.description || '-'}
                      </TableCell>
                      <TableCell>{getProcessWorkAreaLabel(p)}</TableCell>
                      <TableCell>
                        <Badge
                          variant={p.isActive ? 'default' : 'secondary'}
                          className={
                            p.isActive
                              ? 'bg-green-100 text-green-700 border-green-200'
                              : 'bg-gray-100 text-gray-700 border-gray-200'
                          }
                        >
                          {p.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 justify-end">
                          <PermissionGate route={ROUTE} action="view">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleView(p)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate route={ROUTE} action="edit">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(p)}
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate route={ROUTE} action="delete">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(p)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center mt-6">
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
